import { type Registry as Services, service } from '@ember/service';
import Model, { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import { LATEST } from 'lumen/utils/constants/pi-constant-values';

const MESSAGES = {
  MISSING: 'general.missing',
} as const;

type Message = '' | (typeof MESSAGES)[keyof typeof MESSAGES];

export default class ParityStrategyEvolution extends Model {
  [Type] = 'parity-strategy/evolution' as const;

  @attr('number') declare benchmarkLossScore: number;
  @attr('string') declare extractDate: string;
  @attr('number') declare lossScoreHotels: number;
  @attr('number') declare lossScoreShops: number;
  @attr('string') declare message: Message;
  @attr('number') declare numberOfHotels: number;
  @attr('number') declare numberOfHotelsWithIssues: number;
  @attr('number') declare numberOfShops: number;
  @attr('number') declare numberOfShopsWithIssues: number;

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];
  @service declare psSelections: Services['ps-selections'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get extractDateMoment() {
    return this.hotelAndMarketService.toLocalTime(this.extractDate);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedBenchmarkLossScore() {
    return this.intl.formatNumber(this.benchmarkLossScore, {
      style: 'percent',
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedLossScore() {
    return this.intl.formatNumber(this.lossScore, { style: 'percent' });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLatest() {
    return this.extractDate === LATEST;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMissing() {
    return this.message === MESSAGES.MISSING;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get lossScore() {
    return this.psSelections.isHotelSelected
      ? this.lossScoreShops
      : this.lossScoreHotels;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberOfRecords() {
    return this.psSelections.isHotelSelected
      ? this.numberOfShops
      : this.numberOfHotels;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberOfRecordsWithIssues() {
    return this.psSelections.isHotelSelected
      ? this.numberOfShopsWithIssues
      : this.numberOfHotelsWithIssues;
  }
}
